import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button, Card, ErrorState, Heading, Select, Spinner, Stack, Text, TextField } from '../../ui';
import { formatLabel, scoringMethodLabel } from '../../racing/format';
import { useRaceEvent } from '../../racing/queries';
import { useCreateRaceEvent, useUpdateRaceEvent } from '../../racing/mutations';
import type { RaceFormat, SaveRaceEventBody, ScoringMethod } from '../../racing/types';

/** The competition structures offered in the UI. */
const FORMAT_OPTIONS: readonly RaceFormat[] = ['SingleElimination', 'DoubleElimination', 'HeatsThenFinal', 'TimeTrial'];

/** The scoring metrics valid for each structure. */
function scoringOptionsFor(format: RaceFormat): readonly ScoringMethod[] {
  switch (format) {
    case 'SingleElimination':
    case 'DoubleElimination':
      return ['HeadToHeadTime', 'DialInHandicap'];
    default:
      return ['HeadToHeadTime', 'MostLapsThenTime'];
  }
}

interface FormState {
  readonly title: string;
  readonly format: RaceFormat;
  readonly scoringMethod: ScoringMethod;
  readonly startsAt: string;
  readonly heatCount: string;
  readonly advancersPerHeat: string;
}

const INITIAL: FormState = {
  title: '',
  format: 'SingleElimination',
  scoringMethod: 'HeadToHeadTime',
  startsAt: '',
  heatCount: '4',
  advancersPerHeat: '2',
};

/**
 * The create-and-edit form for a race event.
 *
 * @returns The page element.
 */
export function RaceEventFormPage(): React.JSX.Element {
  const { raceEventId } = useParams<{ raceEventId: string }>();
  const isEdit = raceEventId !== undefined;
  const navigate = useNavigate();

  const existing = useRaceEvent(raceEventId ?? '');
  const create = useCreateRaceEvent();
  const update = useUpdateRaceEvent(raceEventId ?? '');

  const [values, setValues] = useState<FormState>(INITIAL);
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Seed the form from the loaded event exactly once.
  if (isEdit && existing.data !== undefined && loadedId !== existing.data.id) {
    const event = existing.data;
    setValues({
      title: event.title,
      format: event.format,
      scoringMethod: event.scoringMethod,
      startsAt: toLocalInput(event.startsAt),
      heatCount: event.heatCount?.toString() ?? '4',
      advancersPerHeat: event.advancersPerHeat?.toString() ?? '2',
    });
    setLoadedId(event.id);
  }

  if (isEdit && existing.isPending) {
    return <Spinner label="Loading the race event" />;
  }

  if (isEdit && existing.isError) {
    return <ErrorState message={existing.error.message} onRetry={() => void existing.refetch()} />;
  }

  const isHeats = values.format === 'HeatsThenFinal';

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);

    const nextErrors: Record<string, string> = {};
    if (values.title.trim() === '') {
      nextErrors['title'] = 'A title is required.';
    }
    if (values.startsAt === '' || Number.isNaN(Date.parse(values.startsAt))) {
      nextErrors['startsAt'] = 'A valid start date and time is required.';
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    const body: SaveRaceEventBody = {
      title: values.title.trim(),
      format: values.format,
      scoringMethod: values.scoringMethod,
      startsAt: new Date(values.startsAt).toISOString(),
      heatCount: isHeats ? Number.parseInt(values.heatCount, 10) : null,
      advancersPerHeat: isHeats ? Number.parseInt(values.advancersPerHeat, 10) : null,
      version: isEdit ? (existing.data?.version ?? null) : null,
    };

    try {
      const saved = isEdit ? await update.mutateAsync(body) : await create.mutateAsync(body);
      await navigate(`/racing/${saved.id}`);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Could not save the race event.');
    }
  };

  const onFormatChange = (format: RaceFormat): void => {
    const options = scoringOptionsFor(format);
    setValues((current) => ({
      ...current,
      format,
      scoringMethod: options.includes(current.scoringMethod) ? current.scoringMethod : options[0]!,
    }));
  };

  return (
    <Stack gap="lg">
      <Heading level={1}>{isEdit ? 'Edit race event' : 'Host a race'}</Heading>
      <Card ariaLabel="Race event details">
        <form onSubmit={(event) => void submit(event)}>
          <Stack gap="lg">
            <TextField
              label="Title"
              value={values.title}
              onChange={(event) => setValues({ ...values, title: event.target.value })}
              error={errors['title']}
            />

            <Select
              label="Format"
              value={values.format}
              onChange={(event) => onFormatChange(event.target.value as RaceFormat)}
              options={FORMAT_OPTIONS.map((format) => ({ value: format, label: formatLabel(format) }))}
            />

            <Select
              label="Scoring"
              value={values.scoringMethod}
              onChange={(event) => setValues({ ...values, scoringMethod: event.target.value as ScoringMethod })}
              options={scoringOptionsFor(values.format).map((method) => ({
                value: method,
                label: scoringMethodLabel(method),
              }))}
            />

            <TextField
              label="Starts at"
              type="datetime-local"
              value={values.startsAt}
              onChange={(event) => setValues({ ...values, startsAt: event.target.value })}
              error={errors['startsAt']}
            />

            {isHeats && (
              <Stack direction="row" gap="md" wrap>
                <TextField
                  label="Heats"
                  type="number"
                  inputMode="numeric"
                  value={values.heatCount}
                  onChange={(event) => setValues({ ...values, heatCount: event.target.value })}
                />
                <TextField
                  label="Advance per heat"
                  type="number"
                  inputMode="numeric"
                  value={values.advancersPerHeat}
                  onChange={(event) => setValues({ ...values, advancersPerHeat: event.target.value })}
                />
              </Stack>
            )}

            {submitError !== null && (
              <p className="gm-field-error" role="alert">
                {submitError}
              </p>
            )}

            <Stack direction="row" gap="sm">
              <Button type="submit" variant="primary" disabled={create.isPending || update.isPending}>
                {isEdit ? 'Save changes' : 'Create race'}
              </Button>
              <Button type="button" onClick={() => void navigate('/racing')}>
                Cancel
              </Button>
            </Stack>

            <Text muted size="sm">
              You can register contestants and generate the bracket once the race is created.
            </Text>
          </Stack>
        </form>
      </Card>
    </Stack>
  );
}

/** Converts an ISO instant to the `YYYY-MM-DDTHH:mm` a datetime-local input expects, in local time. */
function toLocalInput(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const pad = (value: number): string => value.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
